import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { PairingScreen } from './device/PairingScreen';
import { SignInScreen } from './device/SignInScreen';
import { useDeviceToken } from './device/useDeviceToken';
import { useOperator } from './device/useOperator';
import { HubScreen } from './hub/HubScreen';

// One lazy chunk per tool, keyed on the frozen tool ids — tools load on first
// tap, keeping the hub bundle small on warehouse tablets.
const PickRoute = lazy(() => import('./tools/pick/PickRoute'));
const XferRoute = lazy(() => import('./tools/xfer/XferRoute'));
const AdjRoute = lazy(() => import('./tools/adj/AdjRoute'));
const PurRoute = lazy(() => import('./tools/pur/PurRoute'));
const AuditRoute = lazy(() => import('./tools/audit/AuditRoute'));
const FindRoute = lazy(() => import('./tools/find/FindRoute'));

function Loading(): JSX.Element {
  return (
    <div className="kiosk">
      <div className="card">
        <p className="dim">Loading…</p>
      </div>
    </div>
  );
}

/**
 * Device-token gate → operator gate → tool routing.
 *
 * No device token → the pairing screen, whatever the URL. Paired but nobody
 * signed in → the PIN pad, laid OVER the routes (not instead of them) so a
 * mid-task expiry keeps the operator's screen mounted: they sign back in and
 * carry on where they were. Paired + signed in → the hub plus one lazy route
 * per tool.
 */
export function App(): JSX.Element {
  const token = useDeviceToken();
  const operator = useOperator();

  if (token === null) {
    return <PairingScreen />;
  }

  return (
    <>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<HubScreen />} />
          <Route path="/tools/pick" element={<PickRoute />} />
          <Route path="/tools/xfer" element={<XferRoute />} />
          <Route path="/tools/adj" element={<AdjRoute />} />
          <Route path="/tools/pur" element={<PurRoute />} />
          <Route path="/tools/audit" element={<AuditRoute />} />
          <Route path="/tools/find" element={<FindRoute />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      {operator === null && (
        <div className="signin-overlay">
          <SignInScreen />
        </div>
      )}
    </>
  );
}
